import React, { useEffect } from 'react';
import { graphql } from 'gatsby';
import {
  INavOption,
  PageNav,
  SanitySlice,
  SEO,
  ThemeColorVariableInjector
} from '@mayfield/website/components';
import { ICentre, IColor } from '@mayfield/sanity';
import {
  AtfImageText,
  BookTour,
  Faqs,
  ImageGallery,
  Reviews
} from '@mayfield/website/slices';
import { useApp } from '@mayfield/website/hooks';
import { toKebabCase } from '@mayfield/common/utils';
import CentreInfo from './slices/CentreInfo';
import FeesAndInclusions from './slices/FeesAndInclusions';
import InfoClusters from './slices/InfoClusters';
import TeamProfile from './slices/TeamProfile';
import Enquire from './slices/Enquire';

interface IProps {
  data: {
    sanityCentre: ICentre;
  };
}

const Centre = ({ data: { sanityCentre } }: IProps) => {
  const {
    title,
    h1Content,
    customerId,
    atfImage,
    address,
    centreInfo,
    theme,
    location,
    faqs,
    feesAndInclusions,
    imageGalleryItems,
    infoClusters,
    reviews,
    teamProfileArray,
    contactForm,
    customContent
  } = sanityCentre;

  const { setBookATourSelectedCentre } = useApp();

  const pageNavItems: INavOption[] = [
    { sectionId: 'centreInfo', title: 'Centre Details' }
  ];

  if (infoClusters) {
    pageNavItems.push({
      title: 'Age Groups',
      sectionId: 'infoClusters'
    });
  }

  if (feesAndInclusions) {
    pageNavItems.push({
      title: 'Inclusions & Fees',
      sectionId: 'feesAndInclusions'
    });
  }

  if (imageGalleryItems?.[0]) {
    pageNavItems.push({
      title: 'Gallery',
      sectionId: 'imageGallery'
    });
  }

  if (teamProfileArray?.[0]) {
    pageNavItems.push({
      title: 'Meet the Team',
      sectionId: 'teamProfile'
    });
  }

  if (faqs?.[0]) {
    pageNavItems.push({
      title: 'FAQ',
      sectionId: 'faqs'
    });
  }

  if (reviews?.[0]) {
    pageNavItems.push({
      title: 'Reviews',
      sectionId: 'reviews'
    });
  }

  pageNavItems.push({
    title: 'Contact Us',
    sectionId: 'centreContactForm'
  });

  useEffect(() => {
    setBookATourSelectedCentre({
      label: title,
      value: title
    });
  }, [setBookATourSelectedCentre, title]);

  return (
    <>
      <ThemeColorVariableInjector themeColor={theme} />

      <AtfImageText
        data={{
          title: h1Content || title,
          backgroundImage: atfImage,
          shortVersion: false
        }}
      />

      <PageNav themeColor={theme} navOptions={pageNavItems} />

      <CentreInfo
        id="centreInfo"
        {...centreInfo}
        centreTitle={title}
        themeColor={theme}
        address={address}
        location={location}
      />

      {infoClusters && (
        <InfoClusters
          title="Age Groups"
          id="infoClusters"
          infoClusters={infoClusters}
        />
      )}

      {feesAndInclusions && (
        <FeesAndInclusions
          id="feesAndInclusions"
          {...feesAndInclusions}
          themeColor={theme}
        />
      )}

      {customContent?.slices.map((slice: any) => (
        <SanitySlice sliceKey={slice._key} key={slice._key} data={slice} />
      ))}

      {imageGalleryItems?.[0] && (
        <ImageGallery
          id="imageGallery"
          data={{ galleryItems: imageGalleryItems }}
        />
      )}

      {teamProfileArray?.[0] && (
        <TeamProfile
          id="teamProfile"
          profiles={teamProfileArray}
          centreThemeColor={theme}
          centreTitle={title}
        />
      )}

      {faqs?.[0] && (
        <Faqs
          id="faqs"
          data={{
            questions: faqs,
            backgroundColor: { hex: 'var(--color-buff-darker)' } as IColor,
            textColor: { hex: 'var(--color-theme-dark)' } as IColor,
            title: 'Frequently Asked Questions'
          }}
        />
      )}

      {reviews?.[0] && <Reviews id="reviews" data={{ reviews: reviews }} />}

      <Enquire
        databaseID={customerId}
        color={theme}
        centreTitle={title}
        {...contactForm}
      />

      <BookTour />
    </>
  );
};

export default Centre;

export const query = graphql`
  query Centre($id: String!) {
    sanityCentre(id: { eq: $id }) {
      title
      h1Content
      customerId
      theme
      teamProfileArray {
        title
        text
        image {
          asset {
            gatsbyImageData(placeholder: BLURRED)
          }
          altText
        }
        themeColor
      }
      slug {
        current
      }
      address {
        streetAddress
        suburb
        postcode
        state
      }
      location {
        lat
        lng
      }
      reviews {
        review
        rating
        name
      }
      infoClusters {
        text
        subheading
        heading
        graphic {
          video {
            asset {
              url
            }
          }
        }
      }
      imageGalleryItems {
        image {
          altText
          asset {
            gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
          }
        }
        caption
      }
      feesAndInclusions {
        inclusions
        fees {
          ratePerDay
          ageGroup
        }
        inclusionsDisclaimer
        feesDisclaimer
      }
      customContent {
        ...WebsitePagebuilderFragment
      }
      faqs {
        question
        answer
      }
      centreInfo {
        openingHours
        instagram
        facebook
        header
        _rawDescription
        contactNumber
        logo {
          asset {
            gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
            url
          }
        }
      }
      atfImage {
        ...ResponsiveImageFragment
      }
      contactForm {
        successMessage
        subheader
        header
        buttonCTA
      }
      seo {
        ...SeoFragment
      }
    }
  }
`;

export const Head = ({ data: { sanityCentre } }: IProps) => {
  const {
    slug: { current: slug },
    title,
    seo,
    address
  } = sanityCentre;

  const pathname = `/centres/${toKebabCase(address.state)}/${slug}/`;

  const seoTitle = seo?.title || title;

  return (
    <SEO
      pageTitle={seoTitle}
      pathname={pathname}
      description={seo?.description}
      keywords={seo?.keywords}
      openGraphImageUrl={seo?.image?.asset.url}
    />
  );
};
